import React, { useState } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, LinearProgress, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Alert, Divider, Card, CardContent,
} from '@mui/material';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp as TrendingIcon,
  Psychology as ModelIcon,
  BugReport as DriftIcon,
  Assessment as KpiIcon,
  AutorenewRounded as RetrainIcon,
} from '@mui/icons-material';

const modelKpis = [
  { label: 'GINI Coefficient', value: '0.68', target: '> 0.60', status: 'good', desc: 'Discrimination power between defaults and non-defaults' },
  { label: 'AUC-ROC', value: '0.84', target: '> 0.80', status: 'good', desc: 'Area under ROC curve — model classification accuracy' },
  { label: 'KS Statistic', value: '0.41', target: '> 0.35', status: 'good', desc: 'Kolmogorov-Smirnov separation between good and bad accounts' },
  { label: 'Brier Score', value: '0.09', target: '< 0.15', status: 'good', desc: 'Calibration measure of probability forecast accuracy' },
  { label: 'PSI (Population Stability)', value: '0.08', target: '< 0.10', status: 'good', desc: 'Feature distribution drift vs training data baseline' },
  { label: 'Model Default Rate', value: '4.2%', target: '< 6%', status: 'good', desc: 'Actual 90-day default rate on approved loans' },
];

const modelVersions = [
  { version: 'v2.3 (Current)', trainedOn: 'Jan 2026', dataPoints: '84,200', auc: 0.84, gini: 0.68, status: 'Production' },
  { version: 'v2.2', trainedOn: 'Oct 2025', dataPoints: '71,500', auc: 0.82, gini: 0.65, status: 'Archived' },
  { version: 'v2.1', trainedOn: 'Jul 2025', dataPoints: '60,200', auc: 0.79, gini: 0.61, status: 'Archived' },
  { version: 'v2.4 (Staging)', trainedOn: 'Mar 2026', dataPoints: '97,000', auc: 0.86, gini: 0.71, status: 'Staging' },
];

const performanceTrend = [
  { month: 'Sep 25', auc: 0.79, gini: 0.61, defaultRate: 5.8 },
  { month: 'Oct 25', auc: 0.80, gini: 0.63, defaultRate: 5.4 },
  { month: 'Nov 25', auc: 0.81, gini: 0.63, defaultRate: 5.1 },
  { month: 'Dec 25', auc: 0.82, gini: 0.65, defaultRate: 4.9 },
  { month: 'Jan 26', auc: 0.83, gini: 0.67, defaultRate: 4.5 },
  { month: 'Feb 26', auc: 0.84, gini: 0.68, defaultRate: 4.2 },
];

const driftData = [
  { feature: 'Mobile Money Volume', psi: 0.06, status: 'Stable' },
  { feature: 'Transaction Frequency', psi: 0.04, status: 'Stable' },
  { feature: 'Credit Score (Bureau)', psi: 0.09, status: 'Watch' },
  { feature: 'Income Proxy', psi: 0.12, status: 'Drift' },
  { feature: 'Behavioral Stability', psi: 0.03, status: 'Stable' },
  { feature: 'Device Fingerprint', psi: 0.05, status: 'Stable' },
  { feature: 'Utility Payment Score', psi: 0.07, status: 'Watch' },
];

const featureImportance = [
  { feature: 'Mobile Money Regularity', individual: 22, sme: 18 },
  { feature: 'Credit Bureau Score', individual: 20, sme: 12 },
  { feature: 'Income Proxy', individual: 16, sme: 8 },
  { feature: 'Cash Flow Ratio', individual: 5, sme: 24 },
  { feature: 'Repayment History', individual: 12, sme: 14 },
  { feature: 'Behavioral Stability', individual: 10, sme: 10 },
  { feature: 'Invoice/Sales Data', individual: 2, sme: 16 },
  { feature: 'Utility Payments', individual: 8, sme: 5 },
  { feature: 'SIM Tenure', individual: 5, sme: 3 },
];

const radarData = [
  { metric: 'Discrimination', value: 84 },
  { metric: 'Calibration', value: 91 },
  { metric: 'Stability', value: 78 },
  { metric: 'Coverage', value: 88 },
  { metric: 'Fairness', value: 82 },
  { metric: 'Explainability', value: 90 },
];

export default function ModelPerformancePage() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} color="primary" gutterBottom>
          Model Performance & Monitoring
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          AI/ML model KPIs · Drift detection · Feature importance · Continuous retraining pipeline
        </Typography>
      </Box>

      {/* Model Health Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {modelKpis.slice(0, 4).map(k => (
          <Grid size={{ xs: 6, md: 3 }} key={k.label}>
            <Paper sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #16a34a22', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{k.label}</Typography>
              <Typography variant="h4" fontWeight={800} color="success.dark" sx={{ my: 0.5 }}>{k.value}</Typography>
              <Typography variant="caption" color="text.secondary">Target: {k.target}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<KpiIcon />} iconPosition="start" label="Performance KPIs" />
          <Tab icon={<DriftIcon />} iconPosition="start" label="Drift Detection" />
          <Tab icon={<TrendingIcon />} iconPosition="start" label="Feature Importance" />
          <Tab icon={<RetrainIcon />} iconPosition="start" label="Model Versions" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tab === 0 && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Model Performance Trend</Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={performanceTrend} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" domain={[0.75, 0.90]} tickFormatter={v => v.toFixed(2)} tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" domain={[3, 7]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="auc" stroke="#1565c0" strokeWidth={2} name="AUC-ROC" dot />
                    <Line yAxisId="left" type="monotone" dataKey="gini" stroke="#7b1fa2" strokeWidth={2} name="GINI" dot />
                    <Line yAxisId="right" type="monotone" dataKey="defaultRate" stroke="#dc2626" strokeWidth={2} strokeDasharray="4 2" name="Default Rate %" dot />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Model Health Radar</Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} />
                    <Radar dataKey="value" stroke="#1565c0" fill="#1565c0" fillOpacity={0.25} />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                      <TableRow>
                        <TableCell><strong>Metric</strong></TableCell>
                        <TableCell><strong>Current Value</strong></TableCell>
                        <TableCell><strong>Target</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Description</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modelKpis.map(k => (
                        <TableRow key={k.label} hover>
                          <TableCell><Typography fontWeight={600}>{k.label}</Typography></TableCell>
                          <TableCell><Typography fontWeight={700} color="success.dark">{k.value}</Typography></TableCell>
                          <TableCell><Typography variant="body2" color="text.secondary">{k.target}</Typography></TableCell>
                          <TableCell><Chip label="✓ On Track" size="small" color="success" /></TableCell>
                          <TableCell><Typography variant="caption" color="text.secondary">{k.desc}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}

          {tab === 1 && (
            <Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>Feature Drift Detection</Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Income Proxy</strong> feature shows PSI = 0.12 (exceeds 0.10 threshold). Investigate data source changes before next retraining cycle.
              </Alert>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                    <TableRow>
                      <TableCell><strong>Feature</strong></TableCell>
                      <TableCell><strong>PSI Value</strong></TableCell>
                      <TableCell><strong>Drift Status</strong></TableCell>
                      <TableCell><strong>Visual</strong></TableCell>
                      <TableCell><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {driftData.map(d => (
                      <TableRow key={d.feature} hover>
                        <TableCell><Typography fontWeight={600}>{d.feature}</Typography></TableCell>
                        <TableCell><Typography fontWeight={700}>{d.psi.toFixed(2)}</Typography></TableCell>
                        <TableCell>
                          <Chip
                            label={d.status}
                            size="small"
                            color={d.status === 'Stable' ? 'success' : d.status === 'Watch' ? 'warning' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(d.psi * 500, 100)}
                            sx={{ width: 120, height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: d.status === 'Stable' ? '#16a34a' : d.status === 'Watch' ? '#d97706' : '#dc2626' } }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color={d.status === 'Drift' ? 'error' : 'text.secondary'}>
                            {d.status === 'Stable' ? 'Monitor' : d.status === 'Watch' ? 'Investigate' : 'Retrain required'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>PSI Guide:</strong> &lt;0.10 = Stable (no action) · 0.10–0.20 = Watch (investigate) · &gt;0.20 = Significant drift (retrain required)
              </Alert>
            </Box>
          )}

          {tab === 2 && (
            <Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>Feature Importance — Individual vs SME Models</Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Separate scoring models are maintained for Individuals and SMEs. Feature weights reflect the data availability and predictive power specific to each segment.
              </Alert>
              <ResponsiveContainer width="100%" height={380}>
                <BarChart data={featureImportance} layout="vertical" margin={{ left: 40, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="feature" width={160} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => `${v}%`} />
                  <Legend />
                  <Bar dataKey="individual" name="Individual Model" fill="#1565c0" radius={[0, 3, 3, 0]} />
                  <Bar dataKey="sme" name="SME Model" fill="#7b1fa2" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <Alert severity="success" sx={{ mt: 2 }}>
                <strong>Key insight:</strong> Cash Flow Ratio and Invoice/Sales Data are top predictors for SMEs, while Mobile Money Regularity and Credit Bureau Score dominate for individuals — confirming the importance of segment-specific models.
              </Alert>
            </Box>
          )}

          {tab === 3 && (
            <Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>Model Version Management</Typography>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                    <TableRow>
                      <TableCell><strong>Version</strong></TableCell>
                      <TableCell><strong>Trained On</strong></TableCell>
                      <TableCell><strong>Training Data Points</strong></TableCell>
                      <TableCell><strong>AUC-ROC</strong></TableCell>
                      <TableCell><strong>GINI</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modelVersions.map(m => (
                      <TableRow key={m.version} hover sx={{ bgcolor: m.status === 'Production' ? '#f0fdf4' : m.status === 'Staging' ? '#eff6ff' : 'inherit' }}>
                        <TableCell><Typography fontWeight={700}>{m.version}</Typography></TableCell>
                        <TableCell>{m.trainedOn}</TableCell>
                        <TableCell>{Number(m.dataPoints.replace(',', '')).toLocaleString('en-ZW')}</TableCell>
                        <TableCell><Typography fontWeight={600} color={m.auc >= 0.82 ? 'success.dark' : 'text.secondary'}>{m.auc}</Typography></TableCell>
                        <TableCell><Typography fontWeight={600} color={m.gini >= 0.65 ? 'success.dark' : 'text.secondary'}>{m.gini}</Typography></TableCell>
                        <TableCell>
                          <Chip
                            label={m.status}
                            color={m.status === 'Production' ? 'success' : m.status === 'Staging' ? 'primary' : 'default'}
                            size="small"
                            variant={m.status === 'Archived' ? 'outlined' : 'filled'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>v2.4 (Staging)</strong> shows improved AUC-ROC of 0.86 and GINI of 0.71 on holdout data. Champion/challenger test in progress — scheduled for production promotion in April 2026.
              </Alert>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
